/**
 * The two clubs' brand colours for a game, from the tenant's own game record.
 *
 * Lives in utils because both the Watch page and the Audience Layer's host
 * context need it and the layer may not import from @/Pages.
 *
 * Keyed by team ABBREVIATION rather than by home and away. The colours come
 * from the tenant's record and the box score that consumes them comes from
 * GameSheet: two systems that agree on "SHV" and "COR" but are under no
 * obligation to agree on which club a given game calls home. Keying on the
 * abbreviation means a disagreement renders no colour, where keying on
 * position would render the other team's and look perfectly fine.
 */

export interface TeamColor {
  primary: string | null;
  secondary: string | null;
}

/** Abbreviation to colours. Empty when the record carries neither team. */
export type TeamColors = Record<string, TeamColor>;

export const EMPTY_TEAM_COLORS: TeamColors = {};

export function teamColorsFromGame(game: Record<string, unknown> | null | undefined): TeamColors {
  if (!game) return EMPTY_TEAM_COLORS;

  const out: TeamColors = {};
  for (const side of ["home", "away"] as const) {
    // desc is the field the record fills for most tenants; short is the older
    // name and still the only one populated on some games.
    const abbr = str(game[`${side}_team_desc`]) || str(game[`${side}_team_short`]);
    if (abbr === "") continue;
    out[abbr] = {
      primary: str(game[`${side}_team_primary_color`]) || null,
      secondary: str(game[`${side}_team_secondary_color`]) || null,
    };
  }
  // A stable identity for the empty case: the layer's context is memoized on
  // this, and a fresh {} every render would rebuild its API clients.
  return Object.keys(out).length === 0 ? EMPTY_TEAM_COLORS : out;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}
